import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAuthCanWriteIntervention } from "@/app/store/auth.store";
import {
	type InterventionPrefill,
	useInterventionPanelStore,
} from "@/app/store/intervention-panel.store";
import { useMyProfile } from "@/shared/hooks/use-my-profile";
import { DatePicker } from "@/components/ui/DatePicker/DatePicker";
import {
	consultationNoteSchema,
	ConsultationTarget,
	getDefaultNote,
	noteToFormValues,
	type ConsultationNoteFormValues,
} from "@/features/patients/schemas/consultation-note.schema";
import {
	useCreateIntervention,
	useEditIntervention,
} from "@/shared/hooks/use-intervention-actions";

/* ═══════════════════════════════════════════════════════════════════════════
   Record Form

   ## API Migration Notes

   Currently this single component handles BOTH view mode and create mode
   using uncontrolled inputs + `defaultValue`.  When the real API lands:

   1. Split into two components:
      - `NoteView` — read-only display of a fetched note.  No form fields.
        Renders when `noteId` is provided and the fetch succeeded.
      - `NoteCreateForm` — the current form, used ONLY for new mode
        (`noteId` is undefined).  Use controlled inputs (`value`/`onChange`)
        instead of `defaultValue`.

   2. The panel component fetches the note:
        const { data: note, isLoading } = useQuery({
          queryKey: ['note', noteId],
          queryFn: () => fetchNote(noteId),
          enabled: !!noteId,
        });

   3. Save handler calls `POST /api/notes` instead of `addNote()`.
      On success, invalidate the notes query so the timeline refreshes.

   4. `staff` and `staffRole` should come from the auth context, not
      hard-coded ('홍길동' / '의사').

   5. Remove `useRuntimeNotesStore` import — no longer needed.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Seeds a new 중재 기록 from the 예정 일정 it was started from (PRD 04 §3.4 ②-1, SHCT-599).
 *
 * Only two of the schedule's values have a home on this form:
 *   · 일시 → `callDateTime`, in the `YYYY-MM-DD HH:mm` shape `noteToFormValues` already produces.
 *   · 유형 → the form offers 전화/메시지 while a schedule is 외래·대면·전화·재방문. `call` is the one
 *     exact match; 외래·대면·재방문 are deliberately NOT mapped — the PRD names 유형 as prefilled but
 *     defines no correspondence for those three, and inventing one would put a 유형 on the record that
 *     nobody chose. They keep the form's own default and the operator picks.
 *
 * 환자 comes from the panel and 담당 의료진 is not a field here (auth context), so neither is seeded.
 */
function applySchedulePrefill(
	base: ConsultationNoteFormValues,
	prefill: InterventionPrefill | null,
): ConsultationNoteFormValues {
	if (!prefill) return base;
	const callDateTime = prefill.scheduledAt
		? prefill.scheduledAt.replace("T", " ").slice(0, 16)
		: base.callDateTime;
	return {
		...base,
		...(prefill.scheduleType === "call" ? { type: "call" as const } : {}),
		callDateTime,
	};
}

export function RecordForm({
	note,
	onScheduleAdd,
}: {
	note: ReturnType<typeof useInterventionPanelStore.getState>["note"];
	onScheduleAdd?: () => void;
}) {
	const { t } = useTranslation(["medical", "common", "validation"]);

	const patient = useInterventionPanelStore((s) => s.patient);
	// SHCT-599 — set only when the panel was opened from a 예정 일정 via [중재 시작].
	const prefill = useInterventionPanelStore((s) => s.prefill);

	const defaultValues: ConsultationNoteFormValues = note
		? noteToFormValues(note)
		: applySchedulePrefill(getDefaultNote(patient?.appUser), prefill);

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<ConsultationNoteFormValues>({
		mode: "onBlur",
		resolver: zodResolver(consultationNoteSchema),
		defaultValues,
	});

	// The consultation target's default comes from the patient's appUser and is applied once, by
	// getDefaultNote() above (SHCT-786) — there is no second, per-render derivation to hold it there.

	const itype = useWatch({ control, name: "type" });
	const msgText = useWatch({ control, name: "text" });
	const smsSelected = useWatch({ control, name: "smsMessage" });
	// FE-16: SMS > 90 chars auto-converts to LMS — show a notice.
	const smsIsLms = itype === "message" && (msgText?.length ?? 0) > 90;

	const createMutate = useCreateIntervention();
	const editMutate = useEditIntervention();
	const isSaving = createMutate.isPending || editMutate.isPending;

	const closePanel = useInterventionPanelStore((s) => s.closePanel);

	// FE-10 (PD-05): viewing an existing record is READ-ONLY until the author enters edit mode.
	// PRD §3.4 "본인 작성분만 편집" — only 제목/내용 are editable (BE InterventionEditRequest); the
	// intervention kind/target/channel/일시 are immutable. BE is the hard author gate.
	const isView = !!note;
	const canWrite = useAuthCanWriteIntervention();
	// SHCT-865 — BE stays the hard gate, but it must not be the FIRST gate: relying on
	// it alone let a non-author open edit mode, retype the note and only learn at
	// [저장] that it was never theirs. PRD 04 §7 says 편집 is 본인 작성분만, and 공통 11 §4
	// says an unmet-permission control is disabled + tooltipped, i.e. told up front.
	const { data: me } = useMyProfile(isView);
	// Undefined on either side (profile still loading, or a runtime-only note that has
	// not round-tripped) → treat as NOT the author, so the button opens only on a
	// positive match and never flickers editable while the profile is in flight.
	const isAuthor = me?.id != null && note?.staffId != null && me.id === note.staffId;
	const [editing, setEditing] = useState(false);
	const lockMeta = isView; // type/target/call/channel stay read-only even in edit mode
	const lockText = isView && !editing; // 제목/내용 editable when creating or editing

	const onValid = (values: ConsultationNoteFormValues) => {
		if (!patient) return;

		// Edit an existing note: persist only 제목/내용 (the editable surface).
		if (note && editing) {
			editMutate.mutate(
				{
					patientUid: patient.uid,
					noteId: note.id,
					data: {
						title: values.title?.trim() || undefined,
						body: values.text?.trim() ?? "",
					},
				},
				{
					onSuccess: () => {
						setEditing(false);
						closePanel();
					},
				},
			);
			return;
		}

		// Only persist when creating a NEW note (note is null).
		if (!note) {
			const isCall = values.type === "call";
			const channel = !isCall
				? values.appMessage && values.smsMessage
					? "SMS_APP"
					: values.smsMessage
						? "SMS"
						: values.appMessage
							? "APP"
							: undefined
				: undefined;
			createMutate.mutate(
				{
					patientUid: patient.uid,
					input: {
						type: values.type,
						target: values.consultationTarget === ConsultationTarget.GUARDIAN ? "GUARDIAN" : "SELF",
						channel,
						callDirection: isCall ? values.callDirection : undefined,
						callDuration: isCall ? values.callDuration : undefined,
						title: values.title?.trim() || undefined,
						body: values.text?.trim() ?? "",
						occurredAt:
							isCall && values.callDateTime ? values.callDateTime : undefined,
						// SHCT-599 — PRD 04 v2.27 §3.4 ②-1: a record started from an appointment must be
						// LINKED to it on save; that link is the only thing that makes the appointment 완료.
						// Without it every appointment falls to 미완료 at the next 00:00 no matter how
						// carefully it was written up. `prefill` is set only by [중재 시작], so the top-right
						// [신규 중재] path keeps sending nothing and completes no appointment — the same
						// clause says so explicitly.
						scheduleId: prefill?.scheduleId,
					},
				},
				{
					onSuccess: () => {
						const alsoSchedule = (
							document.getElementById("ivpAlsoSchedule") as HTMLInputElement
						)?.checked;

						closePanel();

						if (alsoSchedule && onScheduleAdd) {
							setTimeout(() => onScheduleAdd(), 400);
						}
					},
				},
			);
			return;
		}
	};

	return (
		<form onSubmit={handleSubmit(onValid)}>
			{/* Meta fields (type/target/call/channel) — immutable once created (PRD §3.4) */}
			<fieldset
				disabled={lockMeta}
				className="border-0 p-0 m-0 [min-inline-size:auto]"
			>
			{/* Type tabs */}
			<div className="tab-group mb-4">
				<label
					className={`tab-group__item${itype === "message" ? " tab-group__item--active" : ""}`}
				>
					<input
						type="radio"
						className="visually-hidden"
						value="message"
						{...register("type")}
					/>
					{t("patients.intervention.form.type.message")}
				</label>
				<label
					className={`tab-group__item${itype === "call" ? " tab-group__item--active" : ""}`}
				>
					<input
						type="radio"
						className="visually-hidden"
						value="call"
						{...register("type")}
					/>
					{t("patients.intervention.form.type.call")}
				</label>
			</div>

		{/* 대상 — SHCT-786. `appUser` decides the value this opens on, and nothing more: PRD 04 §3
		    says "상담 대상: 본인 / 보호자(appUser 기본값)", and MED.002 names this field only to EXEMPT it
		    from the "no preselected default" rule — neither states it is read-only. The prototype's two
		    radios carry no disabled condition either. Locking the other option was ours to invent, and it
		    made a guardian consultation impossible to record. Both are selectable; the derived value is
		    still what the form opens on. (In view mode the enclosing fieldset disables them, as before.) */}
		<div className="form-field">
			<label className="form-field__label">
				{t("patients.intervention.form.target.label")}
			</label>
			<Controller
				name="consultationTarget"
				control={control}
				render={({ field }) => (
					<div className="flex gap-4">
						<label className="inline-flex items-center gap-1.5">
							<input
								type="radio"
								name={field.name}
								value={ConsultationTarget.PATIENT}
								checked={field.value === ConsultationTarget.PATIENT}
								onChange={() => field.onChange(ConsultationTarget.PATIENT)}
								onBlur={field.onBlur}
							/>
							{t("patients.intervention.form.target.patient")}
						</label>
						<label className="inline-flex items-center gap-1.5">
							<input
								type="radio"
								name={field.name}
								value={ConsultationTarget.GUARDIAN}
								checked={field.value === ConsultationTarget.GUARDIAN}
								onChange={() => field.onChange(ConsultationTarget.GUARDIAN)}
								onBlur={field.onBlur}
							/>
							{t("patients.intervention.form.target.guardian")}
						</label>
					</div>
				)}
			/>
		</div>

			{/* Call-specific fields */}
			{itype === "call" && (
				<>
					<div className="form-field">
						<label className="form-field__label">
							{t("patients.intervention.form.callFields.dateTime")}
						</label>
						<DatePicker
							register={register("callDateTime")}
							enableTime
							dateFormat="Y-m-d H:i"
						/>
						{errors.callDateTime && (
							<span className="form-field__error">
								{t(
									errors.callDateTime.message ??
										"validation:call.dateTimeRequired",
								)}
							</span>
						)}
					</div>
					<div className="form-field">
						<label className="form-field__label">
							{t("patients.intervention.form.callFields.duration")}
						</label>
						<input
							type="number"
							className="input"
							min={0}
							max={120}
							step={1}
							disabled={lockMeta}
							{...register("callDuration", { valueAsNumber: true })}
						/>
						{errors.callDuration && (
							<span className="form-field__error">
								{t(
									errors.callDuration.message ??
										"validation:call.durationRequired",
								)}
							</span>
						)}
					</div>
					{/* 방향 (통화 방향) — OUT 발신 / IN 수신, required, no default (MED.002 / PRD 04 §3.4). */}
					<div className="form-field">
						<label className="form-field__label">
							{t("patients.intervention.form.callFields.direction")}
						</label>
						<div className="flex gap-4">
							<label className="inline-flex items-center gap-1.5">
								<input type="radio" value="OUT" {...register("callDirection")} />
								{t("patients.intervention.form.callFields.directionOut")}
							</label>
							<label className="inline-flex items-center gap-1.5">
								<input type="radio" value="IN" {...register("callDirection")} />
								{t("patients.intervention.form.callFields.directionIn")}
							</label>
						</div>
						{errors.callDirection && (
							<span className="form-field__error">
								{t(
									errors.callDirection.message ??
										"validation:call.directionRequired",
								)}
							</span>
						)}
					</div>
				</>
			)}

			{/* Message-specific fields */}
			{itype === "message" && (
				<div className="form-field">
					<label className="form-field__label">
						{t("patients.intervention.form.messageFields.channels")}
					</label>
					<div className="flex gap-4">
						<label className="inline-flex items-center gap-1.5">
							<input type="checkbox" {...register("appMessage")} />
							{t("patients.intervention.form.messageFields.appMessage")}
						</label>
						<label className="inline-flex items-center gap-1.5">
							<input type="checkbox" {...register("smsMessage")} />
							{t("patients.intervention.form.messageFields.sms")}
						</label>
					</div>
					{errors.appMessage && (
						<span className="form-field__error">
							{t(errors.appMessage.message ?? "validation:message.channelRequired")}
						</span>
					)}
				</div>
			)}
			</fieldset>

			{/* 제목 / 내용 — editable when creating or when the author is editing (lockText) */}
			<div className="form-field">
				<label className="form-field__label">
					{t("patients.intervention.form.title")}{" "}
					<span className="reg-field-hint">
						({t("patients.intervention.form.titleHint")})
					</span>
				</label>
				<input
					type="text"
					className="input"
					maxLength={100}
					disabled={lockText}
					{...register("title")}
				/>
				{/* SHCT-845 — the schema already rejects emoji / line breaks / >100 chars here
				    (MED.001; 공통 00 §4.2 "차단 시 인라인 에러로 안내"), but nothing rendered the issue, so a title carrying an emoji
				    blocked the save with no explanation at all. */}
				{errors.title && (
					<span className="form-field__error">
						{t(errors.title.message ?? "validation:common.noEmoji")}
					</span>
				)}
			</div>

			{/* 내용 */}
			<div className="form-field">
				<label className="form-field__label">
					{t("patients.intervention.form.content")}{" "}
					<span className="required">*</span>
				</label>
				<textarea
					rows={10}
					maxLength={1000}
					disabled={lockText}
					{...register("text")}
				/>
				{itype === "message" && smsSelected && smsIsLms && (
					<span className="form-field__hint">
						{t("patients.intervention.form.smsLmsNotice")}
					</span>
				)}
				{errors.text && (
					<span className="form-field__error">
						{t(errors.text.message ?? "validation:message.bodyRequired")}
					</span>
				)}
			</div>

			{/* Action bar */}
			<div className="pt-3 border-t border-[var(--border,#e5e7eb)]">
				{!isView && (
					<label className="mb-3 inline-flex items-center gap-2 text-sm cursor-pointer">
						<input type="checkbox" id="ivpAlsoSchedule" />
						{t("patients.intervention.form.alsoSchedule")}
					</label>
				)}
				<div className="flex justify-end gap-2">
					<button
						type="button"
						className="btn btn--secondary"
						onClick={() => {
							if (editing) {
								// Cancel edit → revert to read-only view without closing the panel.
								setEditing(false);
							} else {
								useInterventionPanelStore.getState().closePanel();
							}
						}}
					>
						{t(
							isView && !editing
								? "common:actions.close"
								: "common:actions.cancel",
						)}
					</button>
					{/* View mode: only the note's own author may enter edit mode (PRD 04 §7).
					    Kept visible-but-disabled with a tooltip rather than hidden, per
					    공통 11 §4 ("권한/조건 미충족 → 회색 처리 + 툴팁"). BE re-checks. */}
					{isView && !editing && canWrite && (
						<button
							type="button"
							className="btn btn--primary"
							disabled={!isAuthor}
							title={isAuthor ? undefined : t("patients.intervention.form.editAuthorOnly")}
							onClick={() => setEditing(true)}
						>
							{t("common:actions.edit")}
						</button>
					)}
					{(!isView || editing) && (
						<button type="submit" className="btn btn--primary" disabled={isSaving}>
							{t("common:actions.save")}
						</button>
					)}
				</div>
			</div>
		</form>
	);
}
